import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { CompactMediaPicker } from '@/features/media';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toSendDesignBody } from '../api/mappers';
import { useSendDesign } from '../hooks/use-order-mutations';
import {
  DESIGN_MESSAGE_MAX,
  sendDesignSchema,
  type SendDesignFormInput,
  type SendDesignFormOutput,
} from '../schemas/design-schema';
import { orderConflictKey } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['mediaId', 'message']);

export interface SendDesignDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Emails the shopper a rendering with a signed approve / request-changes link. */
export function SendDesignDialog({ storeId, order, open, onOpenChange }: SendDesignDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const send = useSendDesign(storeId);
  const form = useForm<SendDesignFormInput, unknown, SendDesignFormOutput>({
    resolver: useI18nResolver(sendDesignSchema),
    defaultValues: { mediaId: '', message: '' },
  });
  const length = form.watch('message').length;

  const close = () => {
    onOpenChange(false);
    send.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await send.mutateAsync({ id: order.id, body: toSendDesignBody(values) });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const conflict = orderConflictKey(error, 'orders:design.errors.conflict');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('orders:design.title', { name: order.name })}</DialogTitle>
          <DialogDescription>
            {t('orders:design.description', { email: order.email ?? t('orders:customer.noEmail') })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="mediaId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:design.media')}</FormLabel>
                  <FormControl>
                    <CompactMediaPicker
                      label={t('orders:design.library')}
                      emptyMessage={t('orders:design.noMedia')}
                      value={field.value}
                      onChange={(mediaId) => {
                        field.onChange(mediaId);
                        form.clearErrors('mediaId');
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:design.message')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={4}
                      maxLength={DESIGN_MESSAGE_MAX}
                      placeholder={t('orders:design.messagePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('orders:design.messageHint', { count: length, max: DESIGN_MESSAGE_MAX })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={send.isPending}>
                {send.isPending ? t('orders:design.submitting') : t('orders:design.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
